import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EvenementsService, CreateEvenementDto, UpdateEvenementDto, Evenement } from '../../services/evenements.service';
import { getImageUrl } from '../../services/api';
import { FaCalendar, FaImage, FaFilePdf, FaSave, FaTimes, FaSpinner } from 'react-icons/fa';

const EvenementFormPage: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(isEditMode);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form fields
    const [titre, setTitre] = useState('');
    const [description, setDescription] = useState('');
    const [lienInscription, setLienInscription] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [isCallForPapers, setIsCallForPapers] = useState(false);
    const [dateSoumission, setDateSoumission] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [pdfFileName, setPdfFileName] = useState<string | null>(null);
    const [existingEvent, setExistingEvent] = useState<Evenement | null>(null);

    useEffect(() => {
        if (isEditMode && id) {
            loadEvent(id);
        }
    }, [id, isEditMode]);

    const loadEvent = async (eventId: string) => {
        try {
            setLoading(true);
            const event = await EvenementsService.findOne(eventId);
            setExistingEvent(event);
            setTitre(event.titre);
            setDescription(event.description || '');
            setLienInscription(event.lienInscription || '');
            setDateDebut(event.dateDebut.split('T')[0]);
            setDateDebut(event.dateDebut.split('T')[0]);
            setDateFin(event.dateFin.split('T')[0]);
            if (event.isCallForPapers) {
                setIsCallForPapers(true);
                setDateSoumission(event.dateSoumission ? event.dateSoumission.split('T')[0] : '');
            }
            if (event.image) setImagePreview(event.image);
            if (event.pdfProgramme) setPdfFileName(event.pdfProgramme.split('/').pop() || 'Programme.pdf');
        } catch (err) {
            setError('Erreur lors du chargement de l\'événement');
            console.error('Failed to load event', err);
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Veuillez sélectionner un fichier image');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert('L\'image ne doit pas dépasser 10 Mo');
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                alert('Veuillez sélectionner un fichier PDF');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert('Le PDF ne doit pas dépasser 10 Mo');
                return;
            }
            setPdfFile(file);
            setPdfFileName(file.name);
        }
    };

    const validateForm = (): boolean => {
        if (!titre.trim()) {
            setError('Le titre est obligatoire');
            return false;
        }
        if (!dateDebut) {
            setError('La date de début est obligatoire');
            return false;
        }
        if (!dateFin) {
            setError('La date de fin est obligatoire');
            return false;
        }
        if (new Date(dateFin) < new Date(dateDebut)) {
            setError('La date de fin doit être supérieure ou égale à la date de début');
            return false;
        }
        if (isCallForPapers && !dateSoumission) {
            setError('La date de soumission est obligatoire pour un appel à communication');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            if (isEditMode && id) {
                const updateData: UpdateEvenementDto = {
                    titre,
                    description: description,
                    lienInscription: lienInscription,
                    dateDebut,
                    dateFin,
                    isCallForPapers,
                    dateSoumission: isCallForPapers ? dateSoumission : dateDebut,
                };
                if (imageFile) updateData.image = imageFile;
                if (pdfFile) updateData.pdfProgramme = pdfFile;

                await EvenementsService.update(id, updateData);
            } else {
                const createData: CreateEvenementDto = {
                    titre,
                    description: description,
                    lienInscription: lienInscription,
                    dateDebut,
                    dateFin,
                    isCallForPapers,
                    dateSoumission: isCallForPapers ? dateSoumission : dateDebut,
                };
                if (imageFile) createData.image = imageFile;
                if (pdfFile) createData.pdfProgramme = pdfFile;

                await EvenementsService.create(createData);
            }

            navigate('/admin/evenements');
        } catch (err: any) {
            console.error('Failed to save event', err);
            setError(err.response?.data?.message || 'Erreur lors de la sauvegarde de l\'événement');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                    <p className="text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEditMode ? 'Modifier l\'événement' : 'Nouvel Événement'}
                </h1>
                <p className="text-gray-600 mt-2">
                    {isEditMode ? 'Modifiez les informations de l\'événement' : 'Créez un nouvel événement'}
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 space-y-6">
                {/* Titre */}
                <div>
                    <label htmlFor="titre" className="block text-sm font-medium text-gray-700 mb-2">
                        Titre de l'événement <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="titre"
                        value={titre}
                        onChange={(e) => setTitre(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ex: Conférence annuelle SAPI 2026"
                        required
                    />
                </div>

                {/* Description */}
                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Décrivez l'événement..."
                    />
                </div>

                {/* Lien d'inscription */}
                <div>
                    <label htmlFor="lienInscription" className="block text-sm font-medium text-gray-700 mb-2">
                        Lien d'inscription complet (URL avec https://) - Facultatif
                    </label>
                    <input
                        type="text"
                        id="lienInscription"
                        value={lienInscription}
                        onChange={(e) => setLienInscription(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="https://forms.gle/... (laissez vide si pas d'inscription)"
                    />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="dateDebut" className="block text-sm font-medium text-gray-700 mb-2">
                            Date de début <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaCalendar className="text-gray-400" />
                            </div>
                            <input
                                type="date"
                                id="dateDebut"
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="dateFin" className="block text-sm font-medium text-gray-700 mb-2">
                            Date de fin <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaCalendar className="text-gray-400" />
                            </div>
                            <input
                                type="date"
                                id="dateFin"
                                value={dateFin}
                                onChange={(e) => setDateFin(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Call for Papers Section */}
                <div className="space-y-4 border-t border-gray-200 py-6">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isCallForPapers"
                            checked={isCallForPapers}
                            onChange={(e) => setIsCallForPapers(e.target.checked)}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
                        />
                        <label htmlFor="isCallForPapers" className="ml-2 block text-sm font-semibold text-gray-700 cursor-pointer">
                            Est-ce un appel à communication ?
                        </label>
                    </div>

                    {isCallForPapers && (
                        <div>
                            <label htmlFor="dateSoumission" className="block text-sm font-medium text-gray-700 mb-2">
                                Date limite de soumission <span className="text-red-500">*</span>
                            </label>
                            <div className="relative max-w-md">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaCalendar className="text-secondary" />
                                </div>
                                <input
                                    type="date"
                                    id="dateSoumission"
                                    value={dateSoumission}
                                    onChange={(e) => setDateSoumission(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                                    required={isCallForPapers}
                                />
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                Les utilisateurs pourront soumettre leurs communications jusqu'à cette date.
                            </p>
                        </div>
                    )}
                </div>

                {/* Image Upload */}
                <div>
                    <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                        Image de l'événement
                    </label>
                    {imagePreview && (
                        <div className="mb-4 relative inline-block">
                            <img
                                src={getImageUrl(imagePreview)}
                                alt="Preview"
                                className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setImageFile(null);
                                    setImagePreview(null);
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg cursor-pointer hover:bg-primary hover:text-white transition-colors">
                            <FaImage />
                            <span>{imagePreview ? 'Changer l\'image' : 'Choisir une image'}</span>
                            <input
                                type="file"
                                id="image"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                        </label>
                        <span className="text-sm text-gray-500">JPG, PNG, WEBP (Max 10 Mo)</span>
                    </div>
                </div>

                {/* PDF Upload */}
                <div>
                    <label htmlFor="pdf" className="block text-sm font-medium text-gray-700 mb-2">
                        Programme (PDF)
                    </label>
                    {pdfFileName && (
                        <div className="mb-4 flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <FaFilePdf className="text-red-600 text-2xl" />
                            <span className="flex-1 text-gray-700">{pdfFileName}</span>
                            <button
                                type="button"
                                onClick={() => {
                                    setPdfFile(null);
                                    setPdfFileName(null);
                                }}
                                className="text-red-500 hover:text-red-700"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg cursor-pointer hover:bg-primary hover:text-white transition-colors">
                            <FaFilePdf />
                            <span>{pdfFileName ? 'Changer le PDF' : 'Choisir un PDF'}</span>
                            <input
                                type="file"
                                id="pdf"
                                accept="application/pdf"
                                onChange={handlePdfChange}
                                className="hidden"
                            />
                        </label>
                        <span className="text-sm text-gray-500">PDF uniquement (Max 10 Mo)</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <>
                                <FaSpinner className="animate-spin" />
                                Enregistrement...
                            </>
                        ) : (
                            <>
                                <FaSave />
                                {isEditMode ? 'Mettre à jour' : 'Créer l\'événement'}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/admin/evenements')}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Annuler
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EvenementFormPage;
